import { EntityAdapter } from '@ngrx/entity';
import { createAction, props } from '@ngrx/store';

import { AppIntegration } from '../../interface/appintegration.interface';
import { Integration } from '../../interface/integration.interface';
import { IntegrationConfigField } from '../../interface/integrationmodal.interface';
import { IntegrationRecordPayload } from '../../models/integrations/integrationrecordpayload.class';
import {
    AppIntegrationState,
    IntegrationOrganizationsState,
    IntegrationsState,
    IntergrationsConfigState,
} from '../interface/integration-state.interface';

export enum IntegrationActionTypes {
    INTEGRATION_SET_ACTIVE_TAB = '[INTEGRATIONS] Set Active Tab',
    INTEGRATION_SET_ORGANIZATION_ENTITY = '[INTEGRATIONS] Set Organization Entity',
    INTEGRATION_SET_CONFIG_ENTITY = '[INTEGRATIONS] Set Config Entity',
    INTEGRATION_GET_RECORDS = '[INTEGRATIONS] Get Records',
    INTEGRATION_SET_RECORDS = '[INTEGRATIONS] Set Records',
    INTEGRATION_TEST_CONFIGURATION = '[INTEGRATIONS] Test Configuration',
    INTEGRATION_SAVE_CONFIGURATION = '[INTEGRATIONS] Save Configuration',
    INTEGRATION_VALIDATE_CONFIGURATION = '[INTEGRATIONS] Validate Configuration',
    INTEGRATION_VALIDATE_CONFIGURATION_SUCCESS = '[INTEGRATIONS] Validate Configuration Success',
    INTEGRATION_TOGGLE_CONFIGURATION = '[INTEGRATIONS] Toggle Configuration',
    INTEGRATION_UPDATE_CONFIGURATION_CHANGE = '[INTEGRATIONS] Update Configuration Change',
    INTEGRATION_PUSH_CONFIGURATION = '[INTEGRATIONS] Push Configuration',
    INTEGRATION_DELETE_CONFIGURATION = '[INTEGRATIONS] Delete Configuration',
    INTEGRATION_REMOVE_CONFIGURATION = '[INTEGRATIONS] Remove Configuration',
    GET_APP_INTEGRATION = '[GET_APP_INTEGRATIO] Get App Integration',
    SET_APP_INTEGRATION = '[SET_APP_INTEGRATIO] Set App Integration',
    DELETE_APP_INTEGRATION_DETAILS = '[DELETE_APP_INTEGRATION_DETAILS] Delete App Integration Details',
    DELETE_APP_INTEGRATION_DETAILS_SUCCESS = '[DELETE_APP_INTEGRATION_DETAILS_SUCCESS] Delete App Integration Details Success',
    GET_INTEGRATION_CONFIGURATIONS = '[INTEGRATIONS] Get Integration Configurations',
    SET_APP_INTEGRATION_CONFIGURATIONS = '[INTEGRATIONS] Set App Integration Configurations',
    SET_INTEGRATIONS_SUPPORTED_TYPES = '[INTEGRATIONS] Set Integrations Supported Types',
    GET_DYNAMIC_PARAMS_KEYS = '[INTEGRATIONS] Get Dynamic Params Keys',
    SET_DYNAMIC_PARAMS_KEYS = '[INTEGRATIONS] Set Dynamic Params Keys',
    GET_CONNECTOR = '[INTEGRATIONS] Get Connector',
    GET_CONNECTORS = '[INTEGRATIONS] Get Connectors',
    SET_CONNECTORS = '[INTEGRATIONS] Set Connectors',
    SUBMIT_CONNECTOR = '[INTEGRATIONS] Submit Connector',
    SET_CONNECTOR = '[INTEGRATIONS] Set Connector',
    SET_CONNECTOR_ID = '[INTEGRATIONS] Set Connector Id',
    DELETE_CONNECTOR = '[INTEGRATIONS] Delete Connector',
    DELETED_CONNECTOR = '[INTEGRATIONS] Deleted Connector',
    GET_INTERNAL_CONFIGS = '[INTEGRATIONS] Get Internal Configs',
    SET_INTERNAL_CONFIGS = '[INTEGRATIONS] Set Internal Configs',
    GET_CLONE_CONNECTOR_DATA = '[INTEGRATIONS] Get Clone Connector Data',
    SET_CLONE_CONNECTOR_DATA = '[INTEGRATIONS] Set Clone Connector Data',
    CLONE_AND_SUBSCRIBE_CONNECTOR = '[INTEGRATIONS] Clone and Subscribe Connector',
    REDIRECT_TO_AUTHORIZATION_URI = '[INTEGRATIONS] Redirect to Authorization URI',
    GET_AUTH_SYSTEM_FIELDS = '[INTEGRATIONS] Get Auth System Fields',
    SET_AUTH_SYSTEM_FIELDS = '[INTEGRATIONS] Set Auth System Fields',
    GET_CONNECTOR_REQUIRED_FIELDS = '[INTEGRATIONS] Get Connector Required Fields',
    SET_CONNECTOR_REQUIRED_FIELDS = '[INTEGRATIONS] Set Connector Required Fields',
    INTEGRATION_FIELD_VALUE_CHANGED = '[INTEGRATIONS] Field Value Changed',
}

export const SetIntegrationOrganizationEntity = createAction(
    IntegrationActionTypes.INTEGRATION_SET_ORGANIZATION_ENTITY,
    props<{
        organizationsEntityAdapter: EntityAdapter<IntegrationOrganizationsState>;
        organizationEntity: IntegrationOrganizationsState;
    }>()
);

export const SetActiveIntegrationTab = createAction(
    IntegrationActionTypes.INTEGRATION_SET_ACTIVE_TAB,
    props<{
        tab: IntegrationsState['activeTab'];
    }>()
);

export const SetIntegrationConfigEntity = createAction(
    IntegrationActionTypes.INTEGRATION_SET_CONFIG_ENTITY,
    props<{
        organizationAdapter: EntityAdapter<IntegrationOrganizationsState>;
        organizationEntity: IntegrationOrganizationsState;
        configsEntityAdapter: EntityAdapter<IntergrationsConfigState>;
        configsEntity: IntergrationsConfigState;
    }>()
);

export const GetIntegrationRecords = createAction(
    IntegrationActionTypes.INTEGRATION_GET_RECORDS,
    props<{
        config?: IntegrationsState['activeTab'];
        isMaster?: boolean;
        dontFetchUsingMasters?: boolean;
        forceFetch?: boolean;
    }>()
);

export const SetIntegrationRecords = createAction(
    IntegrationActionTypes.INTEGRATION_SET_RECORDS,
    props<{
        organizationAdapter: EntityAdapter<IntegrationOrganizationsState>;
        configsAdapter: EntityAdapter<IntergrationsConfigState>;
        integrationsAdapter: EntityAdapter<Integration>;
        records: Integration[];
    }>()
);

export const TestIntegrationConfiguration = createAction(
    IntegrationActionTypes.INTEGRATION_TEST_CONFIGURATION,
    props<{
        payload: {
            configuredData: string;
            appId: string;
            name: string;
            organizationId: string;
            type: string;
        };
    }>()
);

export const SaveIntegrationConfiguration = createAction(
    IntegrationActionTypes.INTEGRATION_SAVE_CONFIGURATION,
    props<{
        payload: IntegrationRecordPayload;
    }>()
);

export const ValidateIntegrationConfiguration = createAction(
    IntegrationActionTypes.INTEGRATION_VALIDATE_CONFIGURATION,
    props<{
        payload: IntegrationRecordPayload;
    }>()
);

export const ValidateIntegrationConfigurationSuccess = createAction(
    IntegrationActionTypes.INTEGRATION_VALIDATE_CONFIGURATION_SUCCESS,
    props<{
        redirectURL: string;
    }>()
);

export const ToggleIntegrationConfiguration = createAction(
    IntegrationActionTypes.INTEGRATION_TOGGLE_CONFIGURATION,
    props<{
        payload: IntegrationRecordPayload;
    }>()
);

export const PushIntegrationConfiguration = createAction(
    IntegrationActionTypes.INTEGRATION_PUSH_CONFIGURATION,
    props<{
        organizationAdapter: EntityAdapter<IntegrationOrganizationsState>;
        configsAdapter: EntityAdapter<IntergrationsConfigState>;
        integrationsAdapter: EntityAdapter<Integration>;
        record: IntegrationRecordPayload;
    }>()
);

export const UpdateIntegrationConfigurationChange = createAction(
    IntegrationActionTypes.INTEGRATION_UPDATE_CONFIGURATION_CHANGE,
    props<{
        organizationAdapter: EntityAdapter<IntegrationOrganizationsState>;
        configsAdapter: EntityAdapter<IntergrationsConfigState>;
        integrationsAdapter: EntityAdapter<Integration>;
        record: IntegrationRecordPayload;
    }>()
);

export const DeleteIntegrationConfiguration = createAction(
    IntegrationActionTypes.INTEGRATION_DELETE_CONFIGURATION,
    props<{
        record: IntegrationRecordPayload;
    }>()
);

export const RemoveIntegrationConfiguration = createAction(
    IntegrationActionTypes.INTEGRATION_REMOVE_CONFIGURATION,
    props<{
        organizationAdapter: EntityAdapter<IntegrationOrganizationsState>;
        configsAdapter: EntityAdapter<IntergrationsConfigState>;
        integrationsAdapter: EntityAdapter<Integration>;
        record: IntegrationRecordPayload;
    }>()
);

export const GetAppIntegration = createAction(
    IntegrationActionTypes.GET_APP_INTEGRATION,
    props<{
        integrationType: string;
        serviceId: string;
    }>()
);

export const SetAppIntegration = createAction(
    IntegrationActionTypes.SET_APP_INTEGRATION,
    props<{
        organizationAdapter: EntityAdapter<IntegrationOrganizationsState>;
        appIntegrationAdapter: EntityAdapter<AppIntegrationState>;
        integrationsAdapter: EntityAdapter<AppIntegration>;
        serviceId: string;
        appIntegrationDetails: any;
    }>()
);

export const DeleteAppIntegrationDetails = createAction(
    IntegrationActionTypes.DELETE_APP_INTEGRATION_DETAILS,
    props<{
        configName: string;
        serviceId: string;
    }>()
);

export const DeleteAppIntegrationDetailsSuccess = createAction(
    IntegrationActionTypes.DELETE_APP_INTEGRATION_DETAILS_SUCCESS,
    props<{
        organizationAdapter: EntityAdapter<IntegrationOrganizationsState>;
        appIntegrationAdapter: EntityAdapter<AppIntegrationState>;
        integrationsAdapter: EntityAdapter<AppIntegration>;
        configName: string;
        serviceId: string;
    }>()
);

export const GetIntegrationsConfig = createAction(IntegrationActionTypes.GET_INTEGRATION_CONFIGURATIONS, props<{ forceFetch?: boolean }>());

export const SetAppIntegrationConfigs = createAction(
    IntegrationActionTypes.SET_APP_INTEGRATION_CONFIGURATIONS,
    props<{
        appIntegrationConfigs: {
            inbound: IntegrationConfigField['options'];
            outbound: IntegrationConfigField['options'];
            masterinbound: IntegrationConfigField['options'];
        };
    }>()
);

export const SetIntegrationsSupportedTypes = createAction(
    IntegrationActionTypes.SET_INTEGRATIONS_SUPPORTED_TYPES,
    props<{
        supportedAuthenticationTypes: IntegrationConfigField['options'];
        supportedGrantTypes: IntegrationConfigField['options'];
        ftpProtocols: IntegrationConfigField['options'];
        customAuthSchema: {
            accessTokenURI: string;
            requestPayload: string;
            headers: string;
            responseSchema: {
                tokenKey: string;
                tokenTypeKey: string;
                expiryKey: string;
            };
            defaultExpiryTime: string;
            authorizationKey: string;
            method: string;
        };
        emailFilterKeys: IntegrationConfigField['options'];
        connectorSourceSystems: IntegrationConfigField['options'];
        paginationSchema: { [property: string]: any };
        useMasterDisabled: boolean;
        masterSchedulerEnabled: boolean;
        clientAuthentications: IntegrationConfigField['options'];
        restApiResponseType: IntegrationConfigField['options'];
        payloadTypes?: any[];
        formDataTypes?: any[];
    }>()
);

export const GetDynamicParamsKeys = createAction(IntegrationActionTypes.GET_DYNAMIC_PARAMS_KEYS);

export const SetDynamicParamsKeys = createAction(
    IntegrationActionTypes.SET_DYNAMIC_PARAMS_KEYS,
    props<{
        dynamicParamsKeys: {
            additionalFieldRequired: boolean;
            fieldType: string;
            key: string;
            value: string;
        }[];
    }>()
);

export const GetConnector = createAction(IntegrationActionTypes.GET_CONNECTOR, props<{ resourceName: string }>());

export const GetConnectors = createAction(IntegrationActionTypes.GET_CONNECTORS);

export const SetConnectors = createAction(
    IntegrationActionTypes.SET_CONNECTORS,
    props<{
        organizationId: string;
        connectors: IntegrationsState['connectors']['111'];
    }>()
);

export const SubmitConnector = createAction(
    IntegrationActionTypes.SUBMIT_CONNECTOR,
    props<{
        update?: boolean;
        connector: IntegrationsState['connectors']['111'][0];
    }>()
);

export const SetConnector = createAction(
    IntegrationActionTypes.SET_CONNECTOR,
    props<{
        organizationId: string;
        update?: boolean;
        connector: IntegrationsState['connectors']['111'][0];
    }>()
);

export const SetConnectorId = createAction(
    IntegrationActionTypes.SET_CONNECTOR_ID,
    props<{
        connectorId: string;
    }>()
);

export const DeleteConnector = createAction(
    IntegrationActionTypes.DELETE_CONNECTOR,
    props<{
        resourceName: string;
    }>()
);

export const DeletedConnector = createAction(
    IntegrationActionTypes.DELETED_CONNECTOR,
    props<{
        organizationId: string;
        resourceName: string;
    }>()
);

export const GetInternalConfigs = createAction(
    IntegrationActionTypes.GET_INTERNAL_CONFIGS,
    props<{
        serviceId: string;
        organizationId: string;
        forceFetch?: boolean;
    }>()
);

export const SetInternalConfigs = createAction(
    IntegrationActionTypes.SET_INTERNAL_CONFIGS,
    props<{
        internalConfig: IntegrationConfigField['options'];
    }>()
);

export const GetCloneConnectorData = createAction(IntegrationActionTypes.GET_CLONE_CONNECTOR_DATA, props<{ resourceName: string }>());

export const SetCloneConnectorData = createAction(
    IntegrationActionTypes.SET_CLONE_CONNECTOR_DATA,
    props<{ connector: IntegrationsState['connectors']['111'][0] }>()
);

export const CloneAndSubscribeConnector = createAction(
    IntegrationActionTypes.CLONE_AND_SUBSCRIBE_CONNECTOR,
    props<{
        orgIds: string[];
        connector: IntegrationsState['connectors']['111'][0];
        requiredFields?: { [key: string]: string };
    }>()
);

export const RedirectToAuthorizationURI = createAction(
    IntegrationActionTypes.REDIRECT_TO_AUTHORIZATION_URI,
    props<{ authorizationURI: string }>()
);

export const GetAuthSystemFields = createAction(
    IntegrationActionTypes.GET_AUTH_SYSTEM_FIELDS,
    props<{
        parentFieldId: string;
        authSystemId: string;
    }>()
);

export const SetAuthSystemFields = createAction(
    IntegrationActionTypes.SET_AUTH_SYSTEM_FIELDS,
    props<{
        organizationId: string;
        parentFieldId: string;
        authSystemId: string;
        response: {
            credentialsRequired: boolean;
            requiredFields: {
                [property: string]: string;
            };
        };
    }>()
);

export const GetConnectorRequiredFields = createAction(
    IntegrationActionTypes.GET_CONNECTOR_REQUIRED_FIELDS,
    props<{
        resourceName: string;
    }>()
);

export const SetConnectorRequiredFields = createAction(
    IntegrationActionTypes.SET_CONNECTOR_REQUIRED_FIELDS,
    props<{
        organizationId: string;
        resourceName: string;
        response: {
            credentialsRequired: boolean;
            requiredFields: {
                [property: string]: string;
            };
        };
    }>()
);

export const IntegrationFieldValueChanged = createAction(
    IntegrationActionTypes.INTEGRATION_FIELD_VALUE_CHANGED,
    props<{
        field: IntegrationConfigField;
    }>()
);
